import {
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
	Button,
	Typography,
	Fade,
	Collapse,
	Box
} from "@mui/material";
import {
	Timeline,
	TimelineItem,
	TimelineSeparator,
	TimelineConnector,
	TimelineContent,
	TimelineDot
} from "@mui/lab";
import { timelineItemClasses } from '@mui/lab/TimelineItem';
import { useState, useEffect, useContext } from "react";
import { addUserAcknowledgement } from "../../services/acknowledgement";
import { ACKNOWLEDGEMENT_TYPE_PREVIEW } from "../../services/Constants";
import AuthContext from "../Context/AuthProvider";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";

const steps = [
	{
		emoji: "🔍",
		title: "Filter Profiles",
		description: "Use our filters to find profiles matching your preferences - age, qualifications, location, and more."
	},
	{
		emoji: "👀",
		title: "Preview Profiles",
		description: "Liked a profile? View complete details of 2 profiles for free, including photos and contact information."
	},
	{
		emoji: "💎",
		title: "Unlock Full Access",
		description: "Subscribe to our annual premium plan to enjoy unlimited profile views, powerful search, and your own favorites list."
	}
];

const PreviewModal = ({ open, handleClose, onUpgrade }) => {
	const [showText, setShowText] = useState(false);
	const { getToken, setUserViewedPreviewIntroTime } = useContext(AuthContext);
	const navigate = useNavigate();

	useEffect(() => {
		if (open) {
			const timeout = setTimeout(() => setShowText(true), 200);
			return () => clearTimeout(timeout);
		} else {
			setShowText(false);
		}
	}, [open]);

	const handleDismiss = async () => {
		try {
			const tokenObj = getToken();
			if (tokenObj?.token) {
				const todayDate = new Date().toISOString();
				const response = await addUserAcknowledgement(
					tokenObj.token,
					ACKNOWLEDGEMENT_TYPE_PREVIEW,
					todayDate,
					""
				);

				if (response.data?.addUserAcknowledgement) {
					setUserViewedPreviewIntroTime(todayDate);
				} else {
					throw new Error("Failed to acknowledge preview features");
				}
			}
		} catch (error) {
			console.error("Error acknowledging preview features:", error);
			toast.error("Something went wrong. Please try again later.", { autoClose: 3000 });
		}
		handleClose();
	};

	return (
		<Dialog open={open} onClose={handleDismiss} maxWidth="sm" fullWidth>
			<DialogTitle>Welcome to GSB Soirika Preview experience</DialogTitle>

			<DialogContent dividers sx={{ backgroundColor: "#f9f9f9" }}>
				<Fade in={showText} timeout={1500}>
					<Typography variant="subtitle1" fontWeight="medium" align="center" mb={2}>
						Discover how to make the most of your GSB Soirika experience
					</Typography>
				</Fade>
				<Collapse in={showText} timeout={800}> 
					<Timeline
						position="right"
						sx={{
							[`& .${timelineItemClasses.root}:before`]: {
								flex: 0,
								padding: 0,
							},
						}}
					>
						{steps.map((step, index) => (
							<TimelineItem key={index}>
								<TimelineSeparator>
									<TimelineDot color="primary" variant="outlined">
										<span style={{ fontSize: "1.5rem" }}>{step.emoji}</span>
									</TimelineDot>
									{index !== steps.length - 1 && <TimelineConnector />}
								</TimelineSeparator>

								<TimelineContent>
									<Typography variant="h6" fontWeight="bold" gutterBottom>
										{step.title}
									</Typography>
									<Typography variant="body2" color="textSecondary">
										{step.description}
									</Typography>
								</TimelineContent>
							</TimelineItem>
						))}
					</Timeline>
				</Collapse>
				
				<Box textAlign="center">
					<Typography variant="h6" color="primary" gutterBottom>
						Ready to unlock premium features?
					</Typography>
					<Typography variant="body2" color="textSecondary" mb={2}>
						Get unlimited access to all profiles and premium features
					</Typography>
					<Button 
						variant="contained" 
						color="primary" 
						size="large"
						onClick={onUpgrade}
						sx={{ minWidth: '200px' }}
					>
						Upgrade Now
					</Button>
					<Typography variant="body2" color="textSecondary" mt={2}>
						You can revisit your previewed profiles anytime by clicking
						<Button
							variant="text"
							color="primary"
							sx={{ textTransform: 'none', fontWeight: 600, minWidth: 'auto' }}
							onClick={() => navigate('/previewed-profiles')}
						>
							Previewed Profiles
						</Button>
						in the navigation bar.
					</Typography>
				</Box>
			</DialogContent>

			<DialogActions>
				<Button variant="outlined" onClick={handleDismiss}>
					Maybe Later
				</Button>
			</DialogActions>
		</Dialog>
	);
};

export default PreviewModal; 